import i18n from 'modules/core/lib/i18n'

import { type ManualApproveHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/manualApproveHandler'
import { type DurationUnit } from 'modules/workflows/lib/durationUnit'

export type UIType = 'none' | 'team' | 'manual_selected_employees'

export type StrategyItem = {
  id: string
  type: string
  employeeId?: number
  teamId?: number
}

export const getStrategyOptions = (): { label: string; value: string; ui_type: UIType }[] => [
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.employee_manager'),
    value: 'employee_manager',
    ui_type: 'none' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.admins'),
    value: 'admins',
    ui_type: 'none' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.anyone_in_team'),
    value: 'anyone_in_team',
    ui_type: 'team' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.team_leads'),
    value: 'team_leads',
    ui_type: 'team' as const,
  },
  {
    label: i18n.t('workflows.sidepanel.approval.strategies.manual_selection'),
    value: 'manual_selection',
    ui_type: 'manual_selected_employees' as const,
  },
]

export const getStrategyType = (strategyValue: string): UIType => {
  return getStrategyOptions().find((s) => s.value === strategyValue)?.ui_type || 'none'
}

export const parseStrategiesFromPayload = (
  payload: ManualApproveHandlerSchema['nodeData']['payload']
): StrategyItem[] => {
  const strategies: StrategyItem[] = []

  if (
    payload.reviewer_ids &&
    Array.isArray(payload.reviewer_ids) &&
    payload.reviewer_ids.length > 0
  ) {
    payload.reviewer_ids.forEach((employeeId) => {
      if (typeof employeeId === 'number') {
        strategies.push({
          id: `manual_selection_${employeeId}`,
          type: 'manual_selection',
          employeeId: employeeId,
        })
      }
    })
  }

  if (payload.employee_segments && Array.isArray(payload.employee_segments)) {
    payload.employee_segments.forEach((segment) => {
      const strategyType = getStrategyType(segment.name)

      if (strategyType === 'team' && segment.input && 'team_id' in segment.input) {
        const teamId = segment.input.team_id
        if (typeof teamId === 'number' && teamId > 0) {
          strategies.push({
            id: `${segment.name}_${teamId}`,
            type: segment.name,
            teamId: teamId,
          })
        }
      } else if (strategyType === 'none') {
        strategies.push({
          id: `${segment.name}`,
          type: segment.name,
        })
      }
    })
  }

  return strategies
}

export const strategiesToPayload = (
  strategies: StrategyItem[],
  due_on_value: number,
  due_on_unit: DurationUnit
): ManualApproveHandlerSchema['nodeData']['payload'] => {
  const manualSelectionStrategies = strategies.filter((s) => s.type === 'manual_selection')
  const otherStrategies = strategies.filter((s) => s.type !== 'manual_selection')

  const reviewerIds =
    manualSelectionStrategies.length > 0
      ? manualSelectionStrategies
          .map((s) => s.employeeId)
          .filter((id): id is number => id !== undefined)
      : undefined

  const employeeSegments =
    otherStrategies.length > 0
      ? otherStrategies.map((strategy) => {
          if (
            strategy.teamId &&
            (strategy.type === 'anyone_in_team' || strategy.type === 'team_leads')
          ) {
            const segmentName: 'anyone_in_team' | 'team_leads' =
              strategy.type === 'anyone_in_team' ? 'anyone_in_team' : 'team_leads'
            return {
              name: segmentName,
              input: { team_id: strategy.teamId },
            }
          } else if (strategy.type === 'employee_manager' || strategy.type === 'admins') {
            const segmentName: 'employee_manager' | 'admins' =
              strategy.type === 'employee_manager' ? 'employee_manager' : 'admins'
            return {
              name: segmentName,
              input: {},
            }
          }
          return null
        })
      : undefined

  const filteredEmployeeSegments = employeeSegments?.filter(
    (segment): segment is NonNullable<typeof segment> => segment !== null
  )

  return {
    due_on_value,
    due_on_unit,
    ...(reviewerIds && reviewerIds.length > 0 ? { reviewer_ids: reviewerIds } : {}),
    ...(filteredEmployeeSegments && filteredEmployeeSegments.length > 0
      ? { employee_segments: filteredEmployeeSegments }
      : {}),
  }
}
